import { useAuth } from "@/context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogOut, MessageCircle, Users, User, ShieldCheck } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import Logo from "@/components/Logo";

export default function AppHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const pic = user?.picture && user.picture.startsWith("/api/") ? `${BACKEND}${user.picture}` : user?.picture;

  const isActive = (p) => location.pathname === p;

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200 px-4 sm:px-6 py-3">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
        <Logo size="md" to="/dashboard" testid="app-header-logo" />

        <nav className="flex items-center gap-1">
          <Button
            data-testid="nav-discover-btn"
            variant={isActive("/dashboard") ? "default" : "ghost"}
            className={isActive("/dashboard") ? "bg-teal-600 hover:bg-teal-700 text-white rounded-full" : "rounded-full"}
            onClick={() => navigate("/dashboard")}
          >
            <Users className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Discover</span>
          </Button>
          <Button
            data-testid="nav-chat-btn"
            variant={isActive("/chat") ? "default" : "ghost"}
            className={isActive("/chat") ? "bg-teal-600 hover:bg-teal-700 text-white rounded-full" : "rounded-full"}
            onClick={() => navigate("/chat")}
          >
            <MessageCircle className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Chat</span>
          </Button>
          <Button
            data-testid="nav-plans-btn"
            variant={isActive("/plans") ? "default" : "ghost"}
            className={isActive("/plans") ? "bg-teal-600 hover:bg-teal-700 text-white rounded-full" : "rounded-full"}
            onClick={() => navigate("/plans")}
          >
            <span>Plans</span>
          </Button>
        </nav>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-btn" className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2 py-1 hover:bg-slate-50">
              {pic ? (
                <img src={pic} alt="" className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center text-sm font-semibold">
                  {user?.name?.[0]?.toUpperCase() || "U"}
                </div>
              )}
              <span className="pr-2 text-sm font-medium hidden md:inline">{user?.name?.split(" ")[0] || "You"}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-white border border-slate-200 w-56">
            <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate("/profile")}>
              <User className="w-4 h-4 mr-2" /> Profile
            </DropdownMenuItem>
            {user?.role === "admin" && (
              <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                <ShieldCheck className="w-4 h-4 mr-2" /> Admin Panel
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="text-rose-600 focus:text-rose-700">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
